import ProductDetailsBreadcrumbs from './ProductDetailsBreadcrumbs';
import ProductInfoSection from './ProductInfoSection';
import ProductTechnicalSpecsSection from './ProductTechnicalSpecsSection';
import ProductImagesGallery from './ProductImagesGallery';
import PricingAvailabilityCard from './PricingAvailabilityCard';
import { Skeleton } from 'antd';
import { useGetProductBasicDetailsByAdminQuery } from '../../../store/services/products';
import { useGetCategoryAttributesQuery } from '../../../store/services/lookups';
import { usePublicState } from '../../../store/slices/publicSlice';
import ApproveProductModal from './Modals/ApproveProductModal';
import ReturnProductModal from './Modals/ReturnProductModal';
import RejectProductModal from './Modals/RejectProductModal';
import ActivityLogsList from '@/components/AdminLogs/IndividualLog/ActivityLogsList';

export default function ProductDetailsContent({
  productId,
}: {
  productId: string;
}) {
  const { data, isLoading } = useGetProductBasicDetailsByAdminQuery(productId, {
    skip: !productId,
  });

  const basicDetails = data?.payload;

  const { data: categoryAttributesResp } = useGetCategoryAttributesQuery(
    { categoryId: basicDetails?.categoryId || '' },
    { skip: !basicDetails?.categoryId }
  );

  const { modalId } = usePublicState();

  const categoryAttributes = categoryAttributesResp?.payload || [];

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="seller-product-details-content"
    >
      <ProductDetailsBreadcrumbs
        productId={productId}
        basicDetails={basicDetails}
        loading={isLoading}
      />
      <div
        className="flex flex-col lg:flex-row gap-6 items-start w-full"
        data-testid="seller-product-details-cols-wrapper"
      >
        {/* Left Column - 60% */}
        <div
          className="w-full lg:w-3/5 flex flex-col gap-4 p-6 bg-white dark:bg-gray-900 rounded-[12px] border border-[#E7E7E9] dark:border-gray-800"
          data-testid="seller-product-details-left-col"
        >
          {isLoading ? (
            <Skeleton active title paragraph={{ rows: 6 }} className="mb-4" />
          ) : (
            <ProductInfoSection
              basicDetails={basicDetails}
              categoryAttributes={categoryAttributes}
            />
          )}

          {isLoading ? (
            <Skeleton active title paragraph={{ rows: 8 }} />
          ) : (
            <ProductTechnicalSpecsSection
              basicDetails={basicDetails}
              categoryAttributes={categoryAttributes}
            />
          )}

          <ActivityLogsList id={productId} type="PRODUCT" />
        </div>

        {/* Right Column - 40% */}
        <div
          className="w-full lg:w-2/5 flex flex-col gap-6 lg:sticky lg:top-24"
          data-testid="seller-product-details-right-col"
        >
          {isLoading ? (
            <Skeleton
              active
              title
              paragraph={{ rows: 6 }}
              className="bg-white dark:bg-gray-900 p-6 rounded-2xl border border-gray-200 dark:border-gray-800"
            />
          ) : (
            <ProductImagesGallery basicDetails={basicDetails} />
          )}

          {isLoading ? (
            <Skeleton
              active
              title
              paragraph={{ rows: 6 }}
              className="bg-white dark:bg-gray-900 p-6 rounded-2xl border border-gray-200 dark:border-gray-800"
            />
          ) : (
            <PricingAvailabilityCard basicDetails={basicDetails} />
          )}
        </div>
      </div>
      {modalId === 'APPROVE_PRODUCT_MODAL' && <ApproveProductModal />}
      {modalId === 'RETURN_PRODUCT_MODAL' && <ReturnProductModal />}
      {modalId === 'REJECT_PRODUCT_MODAL' && <RejectProductModal />}
    </div>
  );
}
